import { ChangeDetectionStrategy, Component, HostListener, OnInit, signal, viewChild, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import dayjs from 'dayjs';
import { LoginLogoComponent } from 'projects/components/page-components/login-logo/login-logo.component';
import { TopbarComponent } from 'projects/components/topbar/topbar.component';
import { IncidentEntryComponent } from 'projects/login/src/app/layout/login-layout/components/incident-entry/incident-entry.component';
import { NewsListComponent } from 'projects/login/src/app/layout/login-layout/components/news-list/news-list.component';
import { ServerStatusComponent } from 'projects/login/src/app/layout/login-layout/components/server-status/server-status.component';
import { ServerIncidentsIncident } from 'projects/login/src/app/services/server-status/models/server-incidents-incident';
import { ServerStatusService } from 'projects/login/src/app/services/server-status/server-status.service';
import { SkeletonLoaderDirective } from 'projects/login/src/app/utils/skeleton-loader.directive';
import { ThemeService } from '../../whitelabel/theme.service';
import { IconNames } from 'projects/components/pay-icon/IconNames';
import { NewsItem } from './components/news-item/news-item.component';
import { NewsService } from '../../services/news/news.service';

@Component({
  selector: 'pay-login-login-layout',
  imports: [
    RouterOutlet,
    TranslateModule,
    LoginLogoComponent,
    TopbarComponent,
    ServerStatusComponent,
    SkeletonLoaderDirective,
    NewsListComponent,
    IncidentEntryComponent,
  ],
  templateUrl: './login-layout.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginLayoutComponent implements OnInit {
  private readonly ss = inject(ServerStatusService);
  private _news = inject(NewsService);
  private _lang = inject(TranslateService);
  public static headerText = signal(ThemeService.text);
  protected _headerText = LoginLayoutComponent.headerText;
  activeIncidents = signal<ServerIncidentsIncident[]>([]);
  loadingIncidents = signal(true);
  plannedIncident = signal<ServerIncidentsIncident | undefined>(undefined);
  logoComponent = viewChild(LoginLogoComponent);
  newsItems = signal<NewsItem[]>([]);

  constructor() {
    this._lang.onLangChange.subscribe(() => {
      this.ngOnInit();
    });
  }

  ngOnInit(): void {
    this.getNews();
    this.ss.getIncidents().subscribe({
      next: (response) => {
        this.activeIncidents.set(
          response
            .filter((x) => x.status === 'investigating' || x.status === 'identified' || x.status === 'monitoring')
            .filter((instance) => instance.impact === 'none' || instance.impact === 'minor' || instance.impact === 'major' || instance.impact === 'critical')
            .sort((a, b) => (a.created_at < b.created_at ? 1 : -1)),
        );
        this.plannedIncident.set(
          response
            .filter((x) => dayjs(x.scheduled_for).isAfter(new Date()))
            .filter((instance) => instance.impact === 'maintenance')
            .sort((a, b) => (a.scheduled_for! > b.scheduled_for! ? 1 : -1))[0],
        );
        this.loadingIncidents.set(false);
      },
    });
  }

  getNews() {
    this._news.getAll(this._lang.getCurrentLang()).subscribe({
      next: (data) => {
        this.newsItems.set(
          data.results.map((x) => {
            return {
              date: new Date(x.publishDate),
              title: x.htmlTitle,
              summary: x.metaDescription,
              imageSrc: x.featuredImage,
              id: x.id,
              link: x.url,
            };
          }),
        );
      },
    });
  }

  // on resize
  @HostListener('window:resize')
  onResize(): void {
    if (document.documentElement.clientHeight < 850) {
      this.logoComponent()!.el.nativeElement.classList.add('hidden');
    } else {
      this.logoComponent()!.el.nativeElement.classList.remove('hidden');
    }
  }

  protected readonly IconNames = IconNames;
}
